import { Link } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import type { ReactNode } from 'react';
import { appTabIcons, type AppTabItem } from '@up2d8/ui';
import { useSession } from '../../shared/session';
import { useTenantFeatures } from '../../shared/use-tenant-features';
import { visibleItems, type GatedItem } from '../../shared/tab-visibility';

/**
 * De hoofdbestemmingen van de medewerkerszone, gedeeld door elk hoofdscherm.
 *
 * Vijf sinds Stap 20: Berichten, Agenda, Documenten, Meldingen, Ik. De UI-mockup had op die vijfde
 * plek Chat; die zit sinds Stap 34c in de kopbalk als pictogram met een teller (ADR-0050), omdat
 * één-op-één berichten geen hoofdbestemming zijn maar iets wat je opent als er iets voor je is.
 * Documenten is dat juist wél: een medewerker gaat er naartoe zonder dat er iets nieuws is, om de
 * instructie van zijn machine terug te vinden.
 *
 * **Een tab die nergens heen gaat, staat er niet** (Extra 6, punt 1). Staat de module `agenda` of
 * `documents` uit bij een bedrijf, of zette de klant de functie uit met een vlag, dan verdwijnt de
 * tab in plaats van een leeg scherm te openen. Dat loopt via twee vragen die samen één antwoord
 * geven, en niet via een lijstje uitzonderingen per tab:
 *
 * - **`policy`** — een module die uit staat, registreert zijn rechten niet, dus `has()` is dan al
 *   `false`. Dezelfde vraag dekt meteen de medewerker die het recht niet heeft.
 * - **`flag`** — een module die wél draait terwijl de klant de functie uitzette.
 *
 * Bij twijfel blijft een tab staan (zie `useTenantFeatures`): een lege tab is vervelend, een tab
 * die verdwijnt terwijl de functie werkt, laat iemand zoeken naar iets wat er wél is.
 */

/** Eén hoofdbestemming, met de voorwaarden waaronder ze bestaat. */
interface TabDefinition extends AppTabItem, GatedItem {}

export function useAppTabs(): AppTabItem[] {
  const { t } = useTranslation();
  const { has } = useSession();
  const { isOn } = useTenantFeatures();
  const { Home, CalendarDays, FileText, Bell, User } = appTabIcons;

  const tabs: TabDefinition[] = [
    {
      key: 'home',
      label: t('appNav.home'),
      href: '/app',
      icon: <Home aria-hidden className="h-5 w-5" strokeWidth={2} />,
    },
    {
      key: 'agenda',
      label: t('appNav.agenda'),
      href: '/app/agenda',
      icon: <CalendarDays aria-hidden className="h-5 w-5" strokeWidth={2} />,
      policy: 'agenda.item.read',
    },
    {
      key: 'documents',
      label: t('appNav.documents'),
      href: '/app/documenten',
      icon: <FileText aria-hidden className="h-5 w-5" strokeWidth={2} />,
      policy: 'documents.document.read',
      flag: 'documents.library',
    },
    {
      key: 'notifications',
      label: t('appNav.notifications'),
      href: '/app/notifications',
      icon: <Bell aria-hidden className="h-5 w-5" strokeWidth={2} />,
    },
    {
      key: 'profile',
      label: t('appNav.profile'),
      href: '/app/profile',
      icon: <User aria-hidden className="h-5 w-5" strokeWidth={2} />,
    },
  ];

  return visibleItems(tabs, { has, isOn }).map((tab) => ({
    key: tab.key,
    label: tab.label,
    href: tab.href,
    icon: tab.icon,
  }));
}

/** `renderLink` voor `AppTabBar`/`AppShell`: gebruikt de router-`Link`, geen kale `<a>`. */
export function renderAppTabLink(
  item: AppTabItem,
  className: string,
  children: ReactNode,
): ReactNode {
  return (
    <Link key={item.key} to={item.href} data-touch-target="" className={className}>
      {children}
    </Link>
  );
}
